/**
 * TranscriptMessage — Renders a single event in the interleaved stream
 *
 * Dispatches on event type to render user bubbles, assistant text,
 * thinking blocks, tool cards, and error cards — all in one flat list.
 */
import { isValidElement, useMemo, useState, type ReactNode } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import type { TranscriptEvent, ResolvedRef } from '../../engine/agentTypes'
import { ThinkingIndicator } from './ThinkingIndicator'
import { ToolCallBlock } from './ToolCallBlock'

export interface MessageMetrics {
    startedAt: number
    elapsedMs: number
    firstActivityMs?: number
    tokensOut?: number
    tokensPerSecond?: number
}

interface TranscriptMessageProps {
    event: TranscriptEvent
    isStreaming?: boolean
    streamingText?: string
    isThinkingActive?: boolean
    isPromotingImage?: boolean
    messageMetrics?: MessageMetrics
    now?: number
    // For tool_result lookup
    getToolResult?: (toolId: string) => Extract<TranscriptEvent, { type: 'tool_result' }> | undefined
    onOpenFile?: (path: string) => void
    onPromoteImage?: (event: TranscriptEvent) => void | Promise<void>
}

function RefBadge({ ref: r }: { ref: ResolvedRef }) {
    const isImage = r.isImage || /\.(png|jpe?g|gif|webp|svg|bmp|ico)$/i.test(r.path)
    return (
        <span className="ref-badge" title={r.content ? `${r.size ?? 0} bytes` : 'File not found'}>
            {isImage ? '🖼' : '📄'} {r.path}
        </span>
    )
}

function formatDuration(ms: number): string {
    if (ms < 1000) return `${Math.max(0, Math.round(ms))}ms`
    if (ms < 10_000) return `${(ms / 1000).toFixed(1)}s`
    return `${Math.round(ms / 1000)}s`
}

function formatClock(timestamp: number): string {
    return new Intl.DateTimeFormat(undefined, {
        hour: '2-digit',
        minute: '2-digit',
    }).format(timestamp)
}

function parseErrorMessage(message: string): { summary: string; details?: string } {
    try {
        const parsed = JSON.parse(message)
        if (parsed && typeof parsed === 'object') {
            const record = parsed as Record<string, unknown>
            const summary = typeof record.message === 'string'
                ? record.message
                : typeof record.error === 'string'
                    ? record.error
                    : message
            const details = JSON.stringify(parsed, null, 2)
            return {
                summary,
                details: details !== summary ? details : undefined,
            }
        }
    } catch {
        // Ignore non-JSON error payloads.
    }

    const lines = message.split('\n').map((line) => line.trim()).filter(Boolean)
    if (lines.length <= 1) return { summary: message }
    return {
        summary: lines[0],
        details: lines.slice(1).join('\n'),
    }
}

async function copyTextToClipboard(text: string): Promise<boolean> {
    if (!text) return false

    try {
        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text)
            return true
        }
    } catch {
        // Fall through to textarea copy fallback.
    }

    if (typeof document === 'undefined') return false

    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.setAttribute('readonly', 'true')
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()

    try {
        return document.execCommand('copy')
    } catch {
        return false
    } finally {
        document.body.removeChild(textarea)
    }
}

function extractNodeText(node: ReactNode): string {
    if (node == null || typeof node === 'boolean') return ''
    if (typeof node === 'string' || typeof node === 'number') return String(node)
    if (Array.isArray(node)) return node.map(extractNodeText).join('')
    if (isValidElement<{ children?: ReactNode }>(node)) {
        return extractNodeText(node.props.children)
    }
    return ''
}

function CopyActionButton({
    text,
    label,
    copiedLabel,
    title,
    className,
}: {
    text: string
    label: string
    copiedLabel?: string
    title?: string
    className?: string
}) {
    const [copied, setCopied] = useState(false)

    const handleCopy = async () => {
        const ok = await copyTextToClipboard(text)
        if (!ok) return
        setCopied(true)
        window.setTimeout(() => setCopied(false), 1400)
    }

    return (
        <button
            type="button"
            className={`btn btn-ghost agent-inline-action${className ? ` ${className}` : ''}`}
            onClick={() => { void handleCopy() }}
            title={title ?? label}
        >
            {copied ? (copiedLabel ?? 'Copied') : label}
        </button>
    )
}

function MarkdownPreBlock({ children }: { children?: ReactNode }) {
    const codeText = useMemo(() => extractNodeText(children).replace(/\n$/, ''), [children])

    return (
        <div className="markdown-code-block">
            <div className="markdown-code-toolbar">
                <span className="markdown-code-label">Code</span>
                <CopyActionButton
                    text={codeText}
                    label="Copy code"
                    copiedLabel="Code copied"
                    title="Copy code block"
                />
            </div>
            <pre>{children}</pre>
        </div>
    )
}

export function MarkdownContent({ text }: { text: string }) {
    return (
        <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeHighlight]}
            components={{
                pre: ({ children }) => <MarkdownPreBlock>{children}</MarkdownPreBlock>,
            }}
        >
            {text}
        </ReactMarkdown>
    )
}

export function TranscriptMetricsBar({ metrics }: { metrics: MessageMetrics }) {
    const rate = metrics.tokensPerSecond && Number.isFinite(metrics.tokensPerSecond)
        ? Math.max(0.1, metrics.tokensPerSecond)
        : null

    return (
        <div className="transcript-metrics" aria-label="Response metrics">
            <span className="transcript-metric-chip">Start {formatClock(metrics.startedAt)}</span>
            {metrics.firstActivityMs !== undefined && (
                <span className="transcript-metric-chip">First activity {formatDuration(metrics.firstActivityMs)}</span>
            )}
            <span className="transcript-metric-chip">Elapsed {formatDuration(metrics.elapsedMs)}</span>
            {metrics.tokensOut !== undefined && metrics.tokensOut > 0 && (
                <span className="transcript-metric-chip">{metrics.tokensOut} out tok</span>
            )}
            {rate !== null && (
                <span className="transcript-metric-chip">{rate.toFixed(rate >= 10 ? 0 : 1)} tok/s</span>
            )}
        </div>
    )
}

export function TranscriptMessage({
    event,
    isStreaming,
    streamingText,
    isThinkingActive,
    isPromotingImage: isPromotingImageProp,
    messageMetrics,
    now,
    getToolResult,
    onOpenFile,
    onPromoteImage,
}: TranscriptMessageProps) {
    const [isPromotingImageLocal, setIsPromotingImageLocal] = useState(false)
    const isPromotingImage = Boolean(isPromotingImageProp || isPromotingImageLocal)

    const handlePromoteImage = async (imageEvent: Extract<TranscriptEvent, { type: 'assistant_image' }>) => {
        if (!onPromoteImage || isPromotingImage) return
        setIsPromotingImageLocal(true)
        try {
            await onPromoteImage(imageEvent)
        } finally {
            setIsPromotingImageLocal(false)
        }
    }

    switch (event.type) {
        case 'user_message':
        {
            const hasText = event.text.trim().length > 0
            const hasRefs = event.refs.length > 0
            const attachments = event.attachments ?? []
            const hasAttachments = attachments.length > 0
            const userBubbleClass = `user-bubble${!hasText && hasAttachments ? ' user-bubble-attachments-only' : ''}`

            return (
                <div className="transcript-item transcript-user">
                    <div className={userBubbleClass}>
                        {hasText && <div className="user-text">{event.text}</div>}
                        {hasRefs && (
                            <div className="user-refs">
                                {event.refs.map((r, i) => <RefBadge key={i} ref={r} />)}
                            </div>
                        )}
                        {hasAttachments && (
                            <div className="user-attachments">
                                {attachments.map((att, i) => (
                                    <span key={i} className="user-attachment-badge">
                                        {att.mime.startsWith('image/') ? '🖼' : '📎'} {att.name}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )
        }

        case 'assistant_thinking':
            return (
                <div className="transcript-item transcript-thinking">
                    <ThinkingIndicator
                        text={event.text}
                        isActive={isThinkingActive ?? false}
                        startedAt={event.timestamp}
                        now={now}
                    />
                </div>
            )

        case 'assistant_message_final':
            return (
                <div className="transcript-item transcript-assistant">
                    <div className="assistant-content">
                        <MarkdownContent text={isStreaming ? (streamingText ?? event.text) : event.text} />
                        <div className="assistant-message-footer">
                            <div className="assistant-message-actions">
                                <CopyActionButton
                                    text={event.text}
                                    label="Copy message"
                                    copiedLabel="Message copied"
                                    title="Copy assistant message"
                                />
                            </div>
                            {messageMetrics && <TranscriptMetricsBar metrics={messageMetrics} />}
                        </div>
                    </div>
                </div>
            )

        case 'assistant_stream_delta':
            return (
                <div className="transcript-item transcript-assistant">
                    <div className="assistant-content">
                        <MarkdownContent text={event.text} />
                    </div>
                </div>
            )

        case 'assistant_reasoning_item':
            return (
                <div className="transcript-item transcript-thinking">
                    <ThinkingIndicator
                        text={event.summary?.join('\n') || (event.encryptedContent ? 'Encrypted reasoning item recorded.' : undefined)}
                        isActive={false}
                        startedAt={event.timestamp}
                        now={now}
                    />
                </div>
            )

        case 'tool_call':
            return (
                <div className="transcript-item transcript-tool">
                    <ToolCallBlock
                        call={event}
                        result={getToolResult?.(event.id)}
                        onOpenFile={onOpenFile}
                    />
                </div>
            )

        case 'tool_result':
            // Rendered by ToolCallBlock, skip standalone
            return null

        case 'error':
            {
                const parsed = parseErrorMessage(event.message)
                return (
                    <div className="transcript-item transcript-error">
                        <div className="error-card">
                            <span className="error-icon">⚠️</span>
                            <div className="error-body">
                                <span className="error-text">{parsed.summary}</span>
                                {parsed.details && (
                                    <details className="error-details">
                                        <summary>Details</summary>
                                        <pre className="error-detail-pre">{parsed.details}</pre>
                                    </details>
                                )}
                            </div>
                            {event.retryable && (
                                <button className="btn error-retry-btn">Retry</button>
                            )}
                        </div>
                    </div>
                )
            }

        case 'cost_update':
            // Not rendered inline — updates header badge
            return null

        case 'assistant_image':
            return (
                <div className="transcript-item transcript-assistant-image">
                    <div className="generated-image-card">
                        <img
                            src={event.dataUrl}
                            alt="AI-generated image"
                            className="generated-image"
                        />
                        <div className="generated-image-footer">
                            {event.savedPath && (
                                <span className="generated-image-path">💾 {event.savedPath}</span>
                            )}
                            {event.savedPath && !event.promotedPath && onPromoteImage && (
                                <button
                                    type="button"
                                    className="btn btn-ghost agent-inline-action"
                                    onClick={() => { void handlePromoteImage(event) }}
                                    title={isPromotingImage ? 'Saving image to workspace' : 'Save a copy to workspace'}
                                    disabled={isPromotingImage}
                                >
                                    {isPromotingImage ? 'Saving...' : 'Save to workspace'}
                                </button>
                            )}
                            {event.promotedPath && (
                                <span className="generated-image-promoted">
                                    📁{' '}
                                    <button
                                        type="button"
                                        className="btn btn-ghost agent-inline-action"
                                        onClick={() => onOpenFile?.(event.promotedPath!)}
                                        title="Open promoted file in workspace"
                                    >
                                        {event.promotedPath.split('/').pop()}
                                    </button>
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            )

        default:
            return null
    }
}
